<template>
	<div class="footer">
		<!-- Contact Bar -->
		<div class="footer__contact"><span>CALL US (801) 901-2632</span></div>

		<!-- Content -->
		<div class="footer__body">
			<!-- Logo -->
			<router-link to="/"><AvanaLogo class="footer__logo"/></router-link>

			<div class="footer__content">
				<!-- Links -->
				<div class="content__all-links">
					<!-- Iterate all the link groups -->
					<div v-for="linkGroup in linkGroups" class="content__links" :key="linkGroup.name">
						<!-- Check on the Products iteration if they have the permission to view products -->
						<template v-if="linkGroup.name !== 'Products' || canViewProducts">
							<h6 class="content__links-title">{{ linkGroup.name }}</h6>

							<!-- Iterate the links in the link groups -->
							<template v-for="link in linkGroup.links">
								<!-- Only show the link if the use has the permission to -->
								<router-link
									v-if="!link.hasOwnProperty('permission') || link.permission"
									:to="link.route"
									class="content__link"
									:key="link.name"
									>{{ link.name }}</router-link
								>
							</template>
						</template>
					</div>
				</div>

				<!-- Social -->
				<div class="content__social">
					<a href="https://www.youtube.com/channel/UCSjrmjG6rFHoLXX4fCbMehA" class="social__link" target="_blank"><Youtube /></a>
					<a href="https://www.twitter.com/Avanabottle/" class="social__link" target="_blank"><Twitter /></a>
					<a href="https://www.facebook.com/avanabottle/" class="social__link" target="_blank"><Facebook /></a>
					<a href="https://www.instagram.com/avana/" class="social__link" target="_blank"><Instagram /></a>
					<a href="https://www.pinterest.com/avanabottle/" class="social__link" target="_blank"><Pinterest /></a>
				</div>
			</div>
		</div>

		<!-- Legal Bar -->
		<div class="footer__legal">
			<span>© {{ new Date().getFullYear() }} Trove Brands LLC, All Rights Reserved.</span>
		</div>
	</div>
</template>

<script>
import AvanaLogo from '@/components/icons/AvanaLogo';

// Social
import Youtube from '@/components/icons/Youtube';
import Twitter from '@/components/icons/Twitter';
import Facebook from '@/components/icons/Facebook';
import Instagram from '@/components/icons/Instagram';
import Pinterest from '@/components/icons/Pinterest';

export default {
	components: {
		AvanaLogo,
		Youtube,
		Twitter,
		Facebook,
		Instagram,
		Pinterest
	},
	computed: {
		productLinks() {
			const productLinks = [];

			// Get the product classes
			const productClasses = this.$store.Product.productClasses;

			if (productClasses) {
				// Add all the product classes available to the customer to the array
				for (const productClass of productClasses) {
					productLinks.push({
						name: productClass.name,
						route: { name: 'products', query: { class: productClass.name } }
					});
				}

				// Add the shop all option
				productLinks.push({
					name: 'Shop All',
					route: { name: 'products' }
				});
			}

			return productLinks;
		},
		linkGroups() {
			// Here we can return an array that maps which links we should even show based on the user's permissions, and their labels
			return [
				{ name: 'Products', links: this.productLinks },
				{
					name: 'Account',
					links: [
						{
							name: 'View Company',
							route: { name: 'account.company' },
							permission: this.$store.Role.hasGeneralPermission('view company')
						},
						{
							name: 'View Stores',
							route: { name: 'account.stores' },
							permission: this.$store.Role.hasGeneralPermission('view stores')
						},
						{
							name: 'View Orders',
							route: { name: 'account.orders' },
							permission: this.$store.Role.hasGeneralPermission('view order history')
						},
						{ name: 'Settings', route: { name: 'settings' } }
					]
				},
				{ name: 'Company', links: [{ name: 'Contact Us', route: { name: 'contact' } }] }
			];
		},
		canViewProducts() {
			return this.$store.Role.hasGeneralPermission('view products');
		},
		productClasses() {
			return this.$store.Product.productClasses;
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/brand-container.scss';

.footer {
	color: $brand-gray-100;
	min-height: 500px;
	width: 100vw;
	max-width: 100%;
	background: $brand-gray-400;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	&__contact {
		height: 56px;
		background: #2b2927;
		display: flex;
		align-items: center;
		justify-content: center;
		span {
			font-family: $brand-font-pro;
			font-size: 12px;
			line-height: 16px;
			color: $brand-gray-200;
		}
	}
	&__body {
		@include Brand-Container(md);
		flex: 1;
		margin-top: 28px;
		margin-bottom: 40px;
		@include media-breakpoint-up(md) {
			margin-top: 50px;
			margin-bottom: 50px;
		}
	}
	&__logo {
		font-size: 16px;
		width: 135px;
		fill: $brand-tan;
		color: $brand-tan;
		margin-bottom: 40px;
	}
	&__content {
		@include media-breakpoint-up(md) {
			display: flex;
			justify-content: space-between;
			flex: wrap;
		}
		.content {
			&__all-links {
				display: flex;
				flex-wrap: wrap;
			}
			&__links {
				display: flex;
				flex-direction: column;
				width: 105px;
				margin-bottom: 26px;
				&:not(:last-child) {
					margin-right: 58px;
				}
				&-title {
					font-family: $brand-font-condensed-black;
					text-transform: uppercase;
					font-size: 12px;
					line-height: 26px;
					letter-spacing: 1.3px;
					margin-bottom: 11px;
				}
			}
			&__link {
				color: $brand-tan;
				font-family: $brand-font-pro;
				font-size: 12px;
				line-height: 23px;
				&:hover {
					color: $brand-gray-300;
				}
			}

			&__social {
				.social {
					&__link {
						svg {
							fill: $brand-tan;
							width: auto;
							height: 24px;
							margin-right: 22px;
							&:hover {
								fill: $brand-gray-300;
							}
						}
					}
				}
			}
		}
	}
	&__legal {
		height: 69px;
		background: #2b2927;
		display: flex;
		align-items: center;
		justify-content: center;
		span {
			font-family: $brand-font-pro;
			font-size: 11px;
			line-height: 16px;
			letter-spacing: 0.5px;
			color: $brand-gray-200;
		}
	}
}
</style>
